import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { auditValueLabel } from "@/components/auditLog/auditValueLabel";
import type { AuditLogSummary } from "@/components/auditLog/auditLogTypes";
import styles from "@/components/auditLog/auditLog.module.css";

interface Props {
  entry: AuditLogSummary;
  onOpen: () => void;
}

export default function AuditLogChangesPreview({ entry, onOpen }: Props) {
  const { t } = useTranslation();
  const preview = entry.changes_preview;
  const previewElement = useRef<HTMLDivElement>(null);
  const [clipped, setClipped] = useState(false);
  useEffect(() => {
    const element = previewElement.current;
    if (!element) return;
    const measure = () =>
      setClipped(element.scrollHeight > element.clientHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [preview, t]);
  if (!preview || !Array.isArray(preview.items)) {
    return <span role="alert">{t("audit_diff_preview_unavailable")}</span>;
  }
  return (
    <div className={styles.previewCell}>
      <div className={styles.preview} ref={previewElement}>
        <span className={styles.action}>
          {t(`audit_action_${entry.action}`)}
        </span>
        {preview.items.length === 0 && (
          <span>{t("audit_diff_no_changes")}</span>
        )}
        {preview.items.map((item, index) => (
          <div className={styles.previewField} key={`${item.field}-${index}`}>
            <strong>
              {item.field
                ? t(item.field, { defaultValue: item.field })
                : t("audit_diff_original")}
            </strong>
            {item.operation === "unknown" && (
              <span className={styles.hint}>{t("audit_diff_unknown")}</span>
            )}
            <div
              className={
                item.operation === "changed"
                  ? styles.transition
                  : styles.previewValues
              }
            >
              {item.before && (
                <div className={`${styles.previewValue} ${styles.removed}`}>
                  <span className={styles.marker}>
                    {item.operation === "changed"
                      ? t("audit_diff_before")
                      : `− ${t("audit_diff_removed")}`}
                    :{" "}
                  </span>
                  {auditValueLabel(item.before.kind, item.before.text, t, {
                    fieldName: item.field,
                    literal:
                      item.before.truncated || item.operation === "unknown",
                  })}
                  {item.before.truncated && "…"}
                </div>
              )}
              {item.operation === "changed" && (
                <span className={styles.transitionArrow} aria-hidden="true">
                  →
                </span>
              )}
              {item.after && (
                <div
                  className={`${styles.previewValue} ${
                    item.operation === "unknown"
                      ? styles.original
                      : styles.added
                  }`}
                >
                  <span className={styles.marker}>
                    {item.operation === "changed"
                      ? `${t("audit_diff_after")}: `
                      : item.operation === "unknown"
                      ? ""
                      : `+ ${t("audit_diff_added")}: `}
                  </span>
                  {auditValueLabel(item.after.kind, item.after.text, t, {
                    fieldName: item.field,
                    literal:
                      item.after.truncated || item.operation === "unknown",
                  })}
                  {item.after.truncated && "…"}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className={styles.previewFooter}>
        {(preview.truncated || clipped) && (
          <span className={styles.hint}>
            {t("audit_diff_shortened")}
            {preview.total_fields !== null &&
              ` · ${t("audit_diff_field_count", {
                count: preview.total_fields,
              })}`}
          </span>
        )}
        <GenericButton variant="outlined" size="small" onClick={onOpen}>
          {t("audit_diff_open")}
        </GenericButton>
      </div>
    </div>
  );
}
